import axios from 'axios';
import config from '../config/config';

const BASE_URL = config.API_BASE_URL;

export interface Product {
  uid: string;
  uniqueCode: string;
  title: string;
  description: string;
  information?: string;
  imagePath?: string;
  imageFileName?: string;
  imageFileSize?: number;
  imageFileSizeMB?: number;
  pdfPath?: string;
  pdfFileName?: string;
  pdfFileSize?: number;
  pdfFileSizeMB?: number;
  isPopular: boolean;
  isPremium: boolean;
  isEnergyStar: boolean;
  isActive: boolean;
  isPublished: boolean;
  displayOrder: number;
  viewCount: number;
  category: string;
  manufacturer?: string;
  modelNumber?: string;
  createdTime?: string;
  serverAddTime?: string;
}

export interface Category {
  category: string;
  count: number;
}

class ProductCatalogService {
  private apiClient = axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  /**
   * Get all products
   */
  async getAllProducts(onlyPublished: boolean = true): Promise<{ success: boolean; data: Product[]; message?: string }> {
    try {
      const url = `/ProductCatalog?onlyPublished=${onlyPublished}`;
      console.log('🌐 Fetching products from:', `${BASE_URL}${url}`);

      const response = await this.apiClient.get(url);

      console.log('📡 Raw API Response:', response);
      console.log('📦 Response Data:', response.data);
      console.log('📊 Response Data Type:', typeof response.data, Array.isArray(response.data) ? 'Array' : 'Object');

      // Check for wrapper object with success/data (lowercase)
      if (response.data && response.data.success && response.data.data) {
        const products = response.data.data || [];
        console.log('✅ Detected wrapped response (lowercase) with', products.length, 'products');
        return {
          success: true,
          data: products
        };
      }
      // Check for wrapper object with Status/Data (uppercase) - Web API style
      else if (response.data && response.data.Status && response.data.Data) {
        const products = response.data.Data || [];
        console.log('✅ Detected wrapped response (uppercase) with', products.length, 'products');
        return {
          success: true,
          data: products
        };
      }
      // API returns array directly
      else if (response.data && Array.isArray(response.data)) {
        console.log('✅ Detected array response with', response.data.length, 'products');
        return {
          success: true,
          data: response.data
        };
      }

      console.warn('⚠️ Unexpected response structure');
      return {
        success: false,
        data: [],
        message: 'No products found'
      };
    } catch (error: any) {
      console.error('❌ API Error:', error);
      console.error('❌ Error Response:', error.response?.data);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to load products'
      };
    }
  }

  /**
   * Get all categories
   */
  async getCategories(): Promise<{ success: boolean; data: Category[]; message?: string }> {
    try {
      const response = await this.apiClient.get('/ProductCatalog/categories');

      // Check for wrapper object with success/data (lowercase)
      if (response.data && response.data.success && response.data.data) {
        const categories = response.data.data || [];
        return {
          success: true,
          data: categories
        };
      }
      // Check for wrapper object with Status/Data (uppercase) - Web API style
      else if (response.data && response.data.Status && response.data.Data) {
        const categories = response.data.Data || [];
        return {
          success: true,
          data: categories
        };
      }
      // API returns array directly
      else if (response.data && Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: false,
        data: [],
        message: 'No categories found'
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.message || 'Failed to load categories'
      };
    }
  }

  /**
   * Get specific product by UID
   */
  async getProductByUid(uid: string): Promise<{ success: boolean; data: Product | null; message?: string }> {
    try {
      const response = await this.apiClient.get(`/ProductCatalog/${uid}`);

      // Check for wrapper object with success/data (lowercase)
      if (response.data && response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data
        };
      }
      // Check for wrapper object with Status/Data (uppercase) - Web API style
      else if (response.data && response.data.Status && response.data.Data) {
        const product = response.data.Data;
        return {
          success: true,
          data: product
        };
      }
      // API returns object directly
      else if (response.data && response.data.uid) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: false,
        data: null,
        message: 'Product not found'
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load product'
      };
    }
  }
}

const productCatalogService = new ProductCatalogService();
export default productCatalogService;
